import { useState } from "react";
import {
  Box,
  Menu,
  MenuItem,
  Typography,
  Divider,
} from "@mui/material";
import {
  Dashboard,
  Business,
  ShoppingCart,
  AccountBalance,
  People,
  BarChart,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Settings,
} from "@mui/icons-material";
import { NavLink, useHistory } from "react-router-dom";
import { useAuth } from "./AuthContext";

const HorizontalNavbar = () => {
  const history = useHistory();
  const { permissions } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [nestedAnchorEl, setNestedAnchorEl] = useState(null);
  const [activeNestedMenu, setActiveNestedMenu] = useState(null);

  const hasReadMasterPermission = permissions.includes('Read Master');
  const hasReadTransactionPermission = permissions.includes('Read Transaction');
  const hasReadAccountsPermission = permissions.includes('Read Accounts');
  const hasReadAdministrationPermission = permissions.includes('Read Administration');
  const hasReadReportsPermission = permissions.includes('Read Reports');

  const handleMenuOpen = (event, menuId) => {
    setAnchorEl(event.currentTarget);
    setActiveMenu(menuId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveMenu(null);
    setNestedAnchorEl(null);
    setActiveNestedMenu(null);
  };

  const handleNestedMenuOpen = (event, menuLabel) => {
    event.stopPropagation();
    // Always set the new anchor and menu - this handles switching between submenus
    setNestedAnchorEl(event.currentTarget);
    setActiveNestedMenu(menuLabel);
  };

  const handleNestedMenuClose = () => {
    setNestedAnchorEl(null);
    setActiveNestedMenu(null);
  };

  const handleNavigation = (path) => {
    history.push(path);
    handleMenuClose();
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <Dashboard sx={{ fontSize: 18 }} />,
      route: '/Home',
      permission: true,
    },
    {
      id: 'master',
      label: 'Master',
      icon: <Business sx={{ fontSize: 18 }} />,
      permission: hasReadMasterPermission,
      subItems: [
        {
          label: 'Account Master',
          isNested: true,
          subItems: [
            { label: 'Account Head', route: '/AccountHeadIndex' },
            { label: 'Asset', route: '/AssetIndex' },
            { label: 'Capital', route: '/CapitalIndex' },
            { label: 'Others', route: '/OtherOptionIndex' },
            { label: 'Balance Sheet', route: '/BalanceSheetHeadIndex' },
          ]
        },
        { label: 'Bank', route: '/BankIndex' },
        { label: 'Company', route: '/CompanyEdit' },
        {
          label: 'Master',
          isNested: true,
          subItems: [
            { label: 'Client', route: '/ClientIndex' },
            { label: 'Vendor', route: '/VendorIndex' },
          ]
        },
        {
          label: 'Product',
          isNested: true,
          subItems: [
            { label: 'Category', route: '/ProductCategoryIndex' },
            { label: 'Brand', route: '/ProductBrandIndex' },
            { label: 'Item', route: '/ProductItemIndex' },
            { label: 'Product Relations', route: '/ProductRelationIndex' },
          ]
        },

        // { label: 'Staff', route: '/StaffDashboard' },

      ],
    },
    {
      id: 'transaction',
      label: 'Transaction',
      icon: <ShoppingCart sx={{ fontSize: 18 }} />,
      permission: hasReadTransactionPermission || hasReadMasterPermission,
      subItems: [
        {
          label: 'Purchase',
          isNested: true,
          subItems: [
            // { label: 'Purchase Order', route: '/PurchaseOrderIndex' },
            { label: 'Purchase', route: '/PurchaseIndex' },
            { label: 'Purchase Return', route: '/PurchaseReturnAgainstIndex' },
            // { label: 'Purchase Return', route: '/PurchaseReturnIndex' },
            // { isDivider: true, label: 'Reports' },
            // { label: 'Purchase Report', route: '/PurchaseReport' },
            // { label: 'PO Status Report', route: '/PurchaseOrderReport' },

            // { label: 'Item Wise Purchase', route: '/ItemWisePurchaseReport' },
            // { label: 'Purchase Register', route: '/PurchaseRegister' },
          ]
        },
        {
          label: 'Sales',
          isNested: true,
          subItems: [
            // { label: 'Quotation', route: '/QuotationIndex' },
            // { label: 'Sales Order', route: '/SaleOrderIndex' },
            { label: 'Sales', route: '/SaleIndex' },
            { label: 'Sale Return', route: '/SaleReturnAgainstIndex' },
            // { label: 'Sales Return', route: '/SalesReturnIndex' },
            // { isDivider: true, label: 'Reports' },
            // { label: 'Sales Report', route: '/SalesReport' },
            // { label: 'SO Status Report', route: '/SalesOrderReport' },
            // { label: 'Item Wise Sales', route: '/ItemWiseSalesReport' },
            // { label: 'Sales Register', route: '/SalesRegister' },
            // { label: 'Sales Summary', route: '/SalesSummaryReport' },
          ]
        },
        // {
        //   label: 'Stock',
        //   isNested: true,
        //   subItems: [
        //     { label: 'Stock Transfer In', route: '/StockTransferInIndex' },
        //     { label: 'Stock Transfer Out', route: '/StockTransferOutIndex' },
        //     { isDivider: true, label: 'Reports' },
        //     { label: 'Stock Transfer Report', route: '/StockTransferReport' },
        //     { label: 'Stock Movement Report', route: '/StockMovementReport' },
        //   ]
        // },
      ],
    },
    {
      id: 'accounts',
      label: 'Accounts',
      icon: <AccountBalance sx={{ fontSize: 18 }} />,
      permission: hasReadAccountsPermission,
      subItems: [
        { label: 'Payment & Receipt', route: '/PaymentAndReceiptIndex' },
        { label: 'Journal Entry', route: '/JournalEntryIndex' },
      ],
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: <BarChart sx={{ fontSize: 18 }} />,
      permission: hasReadReportsPermission || hasReadMasterPermission,
      subItems: [
        { label: 'Cash Book', route: '/CashBook' },
        { label: 'Ledger', route: '/Ledger' },
        // { label: 'Vendor Due List', route: '/VendorDueList' },
        // { label: 'Customer Due List', route: '/CustomerDueList' },
        { label: 'Vendor Ageing', route: '/VendorAging' },
        { label: 'Customer Ageing', route: '/CustomerAging' },
        { label: 'Outstanding', route: '/Outstanding' },
        { label: 'Income Expense Summary', route: '/IncomeExpenseSummary' },
        { label: 'Purchase Party & ItemWise Report', route: '/PurchasePartyandItemWiseReport' },
        { label: 'Sale Party & ItemWise Report', route: '/SalePartyandItemWiseReport' },
        { label: 'Stock Report', route: '/StockReport' },
        { label: 'Detailed Invoice', route: '/DetailedInvoiceReport' },
      ],
    },
    {
      id: 'auth',
      label: 'Auth',
      icon: <People sx={{ fontSize: 18 }} />,
      permission: hasReadAdministrationPermission,
      subItems: [
        { label: 'Users', route: '/UserIndex' },
        { label: 'Roles', route: '/RoleIndex' },
        { label: 'Reset Password', route: '/PasswordChange' },
      ],
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <Settings sx={{ fontSize: 18 }} />,
      permission: hasReadMasterPermission,
      subItems: [
        { label: 'Logout', route: '/Logout' },
      ],
    },
  ];

  const renderNestedMenu = (parentItem) => (
    <Menu
      anchorEl={nestedAnchorEl}
      open={activeNestedMenu === parentItem.label}
      onClose={handleNestedMenuClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{
        sx: {
          background: "linear-gradient(135deg, #0f172a 0%, #4e024e 50%, #081642ff 100%)",
          color: 'white',
          minWidth: '200px',
          maxHeight: '70vh',
          overflowY: 'auto',
          ml: 0.5,
          borderRadius: '4px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        },
      }}
    >
      {parentItem.subItems?.map((subItem, idx) => (
        subItem.isDivider ? (
          <Box key={idx} sx={{ my: 0.5 }}>
            <Divider sx={{ borderColor: '#FFD700', borderWidth: '1px' }} />
            <Typography
              sx={{
                color: '#FFD700',
                px: 2,
                py: 0.5,
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              {subItem.label}
            </Typography>
          </Box>
        ) : (
          <MenuItem
            key={idx}
            onClick={() => handleNavigation(subItem.route)}
            sx={{
              fontSize: '13px',
              py: 0.75,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
            }}
          >
            {subItem.label}
          </MenuItem>
        )
      ))}
    </Menu>
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '48px', gap: 0 }}>
      {menuItems.map((item) => {
        if (!item.permission) return null;

        if (item.route && !item.subItems) {
          return (
            <Box
              key={item.id}
              component={NavLink}
              to={item.route}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, px: 2, py: 1,
                color: 'white', textDecoration: 'none', cursor: 'pointer',
                fontSize: '13px', fontWeight: 500, height: '100%',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                '&.active': { backgroundColor: 'rgba(255,255,255,0.15)' },
              }}
            >
              {item.icon}
              <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{item.label}</Typography>
            </Box>
          );
        }

        return (
          <Box key={item.id}>
            <Box
              onClick={(e) => handleMenuOpen(e, item.id)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, px: 2, py: 1,
                color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: 500, height: '48px',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                ...(activeMenu === item.id && { backgroundColor: 'rgba(255,255,255,0.15)' }),
              }}
            >
              {item.icon}
              <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{item.label}</Typography>
              <KeyboardArrowDown sx={{ fontSize: 16, ml: 0.5 }} />
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={activeMenu === item.id}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{
                sx: {
                  background: "linear-gradient(135deg, #0f172a 0%, #4e024e 50%, #081642ff 100%)", color: 'white', minWidth: '180px',
                  mt: 0, borderRadius: '0 0 4px 4px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                },
              }}
            >
              {item.subItems?.map((subItem, idx) => (
                subItem.isNested ? (
                  <Box key={idx}>
                    <MenuItem
                      onClick={(e) => handleNestedMenuOpen(e, subItem.label)}
                      sx={{
                        fontSize: '13px', py: 1, display: 'flex', justifyContent: 'space-between',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                        ...(activeNestedMenu === subItem.label && { backgroundColor: 'rgba(255,255,255,0.1)' }),
                      }}
                    >
                      {subItem.label}
                      <KeyboardArrowRight sx={{ fontSize: 16 }} />
                    </MenuItem>
                    {renderNestedMenu(subItem)}
                  </Box>
                ) : (
                  <MenuItem
                    key={idx}
                    onClick={() => handleNavigation(subItem.route)}
                    sx={{ fontSize: '13px', py: 1, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
                  >
                    {subItem.label}
                  </MenuItem>
                )
              ))}
            </Menu>
          </Box>
        );
      })}
    </Box>
  );
};

export default HorizontalNavbar;
